import React, { useState, useRef, useEffect } from 'react'
import { X, Eye, EyeOff, Smartphone, QrCode, Key, CheckCircle, Copy, Download } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'

const MfaSetupWizard = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [enrollData, setEnrollData] = useState(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  const codeInputRef = useRef(null)

  useEffect(() => {
    if (currentStep === 2) {
      setTimeout(() => codeInputRef.current?.focus(), 100)
    }
  }, [currentStep])

  // Auto-start enrollment when component mounts
  useEffect(() => {
    startMfaEnrollment()
  }, [])

  const startMfaEnrollment = async () => {
    setLoading(true)
    setError('')

    try {
      // Get current user first
      const { data: userData, error: userError } = await supabase.auth.getUser()
      if (userError || !userData.user) {
        setError('Please log in again to continue.')
        return
      }

      console.log('🔐 MFA Setup: Starting enrollment for user:', userData.user.email)
      
      // Enroll MFA factor directly using official Supabase MFA API
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'E8 Tracker'
      })
      
      if (error) {
        // Check for common error scenarios
        if (error.message?.includes('factor already exists') || error.message?.includes('friendly name')) {
          setError('MFA is already configured for this account. Please close this wizard and use the "Test MFA" or "Manage" buttons instead.')
          return
        }
        if (error.message?.includes('session')) {
          setError('Session expired. Please close this window and try again.')
          return
        }
        console.error('🔐 MFA enrollment error:', error)
        setError(error.message || 'Failed to start MFA setup. Please try again.')
        return
      }

      console.log('🔐 MFA Setup: Enrollment successful, showing QR code')
      setEnrollData(data)
      setCurrentStep(1) // Show QR code step first
    } catch (err) {
      console.error('🔐 MFA enrollment error:', err)
      setError('Failed to start MFA setup. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerificationSubmit = async (e) => {
    e.preventDefault()
    if (!verificationCode.trim() || verificationCode.length !== 6) return

    setLoading(true)
    setError('')

    try {
      // Create MFA challenge for the enrolled factor
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrollData.id
      })
      
      if (challengeError) {
        setError('Failed to create verification challenge. Please try again.')
        return
      }

      // Verify the TOTP code using the challenge
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrollData.id,
        challengeId: challengeData.id,
        code: verificationCode
      })
      
      if (verifyError) {
        setError('Invalid verification code. Please try again.')
        setVerificationCode('')
        return
      }

      // Generate recovery codes - Supabase doesn't provide these automatically
      // Using crypto-secure random generation
      const codes = Array.from({ length: 8 }, () => {
        const array = new Uint8Array(3)
        crypto.getRandomValues(array)
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase()
      })
      setRecoveryCodes(codes)
      setCurrentStep(3)
    } catch (err) {
      console.error('MFA verification error:', err)
      setError('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'))
    toast.success('Recovery codes copied to clipboard')
  }

  const downloadRecoveryCodes = () => {
    const content = `E8 Tracker - Two-Factor Authentication Recovery Codes

Generated: ${new Date().toLocaleDateString()}

${recoveryCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

Important:
- Save these codes in a secure location
- Each code can only be used once
- Use these codes if you lose access to your authenticator app
- Generate new codes if you suspect these have been compromised`

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'e8-tracker-recovery-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Recovery codes downloaded')
  }

  // Use QR code URL directly from Supabase response
  const qrUrl = enrollData?.totp?.qr_code
  const secret = enrollData?.totp?.secret || ''

  const steps = [
    { number: 1, title: 'Set up authenticator', icon: QrCode },
    { number: 2, title: 'Verify setup', icon: Smartphone },
    { number: 3, title: 'Save recovery codes', icon: CheckCircle }
  ]

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Set up two-factor authentication
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isActive = step.number === currentStep
              const isCompleted = step.number < currentStep
              
              return (
                <div key={step.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    isCompleted 
                      ? 'bg-green-500 border-green-500 text-white'
                      : isActive
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'border-gray-300 text-gray-300'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      step.number < currentStep ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              )
            })}
          </div>

          {/* Step Content */}
          <div className="space-y-6">
            {/* Loading State */}
            {loading && currentStep === 1 && !enrollData && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-sm text-gray-600">Setting up MFA enrollment...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-4">
                <p className="text-sm text-red-600 mb-4">{error}</p>
                <button
                  onClick={startMfaEnrollment}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Step 1: QR Code Setup */}
            {currentStep === 1 && enrollData && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-2">
                  Set up your authenticator app
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Scan this QR code with your authenticator app (like Google Authenticator, Authy, or 1Password).
                </p>
                
                {qrUrl && (
                  <div className="flex justify-center mb-4">
                    <img 
                      src={qrUrl} 
                      alt="MFA QR Code" 
                      className="border rounded-lg p-2 bg-white"
                      width={200}
                      height={200}
                    />
                  </div>
                )}
                
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-600 mb-2">
                    Can't scan the QR code? Enter this code manually:
                  </p>
                  <div className="flex items-center justify-between bg-white rounded border px-2 py-1">
                    <code className="text-sm font-mono text-gray-900 break-all">
                      {secret}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(secret)
                        toast.success('Secret key copied')
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <button
                  onClick={() => setCurrentStep(2)}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  I've added the account
                </button>
              </div>
            )}

            {/* Step 2: Verification */}
            {currentStep === 2 && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-2">
                  Enter the verification code
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Open your authenticator app and enter the 6-digit code for E8 Tracker.
                </p>
                
                <form onSubmit={handleVerificationSubmit}>
                  <input
                    ref={codeInputRef}
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-3 py-3 text-center text-lg font-mono tracking-widest border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                  />
                  
                  {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                  )}
                  
                  <button
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    className="w-full mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'Verify and enable'}
                  </button>
                </form>
              </div>
            )}

            {/* Step 3: Recovery Codes */}
            {currentStep === 3 && (
              <div>
                <h4 className="text-base font-medium text-gray-900 mb-2">
                  Save your recovery codes
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Store these recovery codes in a safe place. You can use them to access your account if you lose your authenticator device.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                    {recoveryCodes.map((code, index) => (
                      <div key={index} className="flex items-center">
                        <span className="text-gray-500 w-4">{index + 1}.</span>
                        <span className="ml-2">{code}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex space-x-3 mb-6">
                  <button
                    onClick={copyRecoveryCodes}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy codes
                  </button>
                  <button
                    onClick={downloadRecoveryCodes}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </button>
                </div>
                
                <button
                  onClick={onComplete}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MfaSetupWizard
